import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import {
  Loader2, AlertCircle, CheckCircle, Users, CreditCard,
  UserPlus, RefreshCw, Copy, Plus, QrCode,
  Download, Search, Edit, Trash2, Mail,
  Bell, ShieldAlert, Calendar, UserX
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL, StatusBadge, channelNames } from './admin/shared';
import { DashboardTab } from './admin/DashboardTab';
import { MembershipsTab } from './admin/MembershipsTab';
import { EmergenciesTab } from './admin/EmergenciesTab';
import { ExpiringTab } from './admin/ExpiringTab';
import { ClientsTab } from './admin/ClientsTab';
import { ProspectsTab } from './admin/ProspectsTab';
import { ActivityLogTab } from './admin/ActivityLogTab';
import { ReferralsTab } from './admin/ReferralsTab';

const CleanupSection = ({ onComplete }) => {
  const [keepEmails, setKeepEmails] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [cleaning, setCleaning] = useState(false);
  const [result, setResult] = useState(null);

  const handleCleanup = async () => {
    if (confirmText !== 'LIMPIAR') return;
    setCleaning(true);
    setResult(null);
    try {
      const emails = keepEmails.split('\n').map(e => e.trim()).filter(e => e.length > 0);
      const response = await axios.post(`${API_URL}/api/admin/cleanup-test-data`, {
        keep_emails: emails, confirm: true
      }, { withCredentials: true });
      setResult(response.data);
      toast.success('Limpieza completada');
      setConfirmText('');
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al limpiar');
    } finally { setCleaning(false); }
  };

  return (
    <div className="space-y-4">
      {result && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle className="h-4 w-4 text-emerald-600" />
          <AlertDescription className="text-emerald-800">
            Eliminados: {result.deleted.users} usuarios, {result.deleted.memberships} membresías, {result.deleted.codes} códigos.
            <br />Conservados: {result.kept_emails.join(', ')}
          </AlertDescription>
        </Alert>
      )}
      <div className="space-y-2">
        <Label>Emails a CONSERVAR (uno por línea)</Label>
        <textarea
          className="w-full border rounded-lg p-3 text-sm h-28 focus:ring-2 focus:ring-red-200 focus:border-red-300"
          placeholder={"admin@sosfindmed.com\nkarlafindmed@gmail.com\ninfrared2629@gmail.com\nnikered@gmail.com"}
          value={keepEmails} onChange={(e) => setKeepEmails(e.target.value)} data-testid="cleanup-keep-emails"
        />
        <p className="text-xs text-gray-400">La cuenta de admin siempre se conserva. Todo lo demás será eliminado.</p>
      </div>
      <div className="space-y-2">
        <Label>Escribe <strong className="text-red-600">LIMPIAR</strong> para confirmar</Label>
        <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Escribe LIMPIAR" data-testid="cleanup-confirm-input" />
      </div>
      <Button variant="destructive" onClick={handleCleanup} disabled={confirmText !== 'LIMPIAR' || cleaning}
        className="bg-red-600 hover:bg-red-700" data-testid="cleanup-submit-btn">
        {cleaning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Limpiando...</> : <><Trash2 className="w-4 h-4 mr-2" />Eliminar Datos de Prueba</>}
      </Button>
    </div>
  );
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [emergencyRequests, setEmergencyRequests] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [expiringSoon, setExpiringSoon] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [conversionStats, setConversionStats] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');

  // Add user form
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [newUserData, setNewUserData] = useState({ name: '', email: '', phone: '', card_code: '', sales_channel: 'admin' });
  const [newUserResult, setNewUserResult] = useState(null);

  // Create codes form
  const [createCodesOpen, setCreateCodesOpen] = useState(false);
  const [creatingCodes, setCreatingCodes] = useState(false);
  const [codesData, setCodesData] = useState({ quantity: 10, sales_channel: 'physical_card', prefix: 'SOSFM' });
  const [createdCodes, setCreatedCodes] = useState(null);

  // Membership detail
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Status change
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);

  // Repair membership for existing user
  const [repairEmail, setRepairEmail] = useState('');
  const [repairingMembership, setRepairingMembership] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  // Diagnostic
  const [diagnostic, setDiagnostic] = useState(null);
  const [loadingDiagnostic, setLoadingDiagnostic] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [dashboardRes, membershipsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dashboard`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/memberships`, { withCredentials: true })
      ]);
      setDashboard(dashboardRes.data);
      setMemberships(membershipsRes.data);

      // Fetch secondary data without blocking
      axios.get(`${API_URL}/api/admin/emergency-requests`, { withCredentials: true })
        .then(r => setEmergencyRequests(r.data)).catch(() => {});
      axios.get(`${API_URL}/api/admin/activity-log`, { withCredentials: true })
        .then(r => setActivityLog(r.data)).catch(() => {});
      axios.get(`${API_URL}/api/admin/expiring-soon?days=60`, { withCredentials: true })
        .then(r => setExpiringSoon(r.data)).catch(() => {});
      axios.get(`${API_URL}/api/admin/users`, { withCredentials: true })
        .then(r => {
          setAllUsers(r.data);
          setProspects(r.data.filter(u => !u.has_membership));
        }).catch(() => {});
      axios.get(`${API_URL}/api/admin/conversion-stats`, { withCredentials: true })
        .then(r => setConversionStats(r.data)).catch(() => {});
      axios.get(`${API_URL}/api/admin/referral-stats`, { withCredentials: true })
        .then(r => setReferralStats(r.data)).catch(() => {});
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error('Error fetching admin data:', err);
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // --- Handler functions ---
  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddingUser(true);
    setNewUserResult(null);
    try {
      const response = await axios.post(`${API_URL}/api/admin/add-user`, newUserData, { withCredentials: true });
      setNewUserResult(response.data);
      toast.success('Usuario creado exitosamente');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear usuario');
    } finally { setAddingUser(false); }
  };

  const handleCreateCodes = async (e) => {
    e.preventDefault();
    setCreatingCodes(true);
    setCreatedCodes(null);
    try {
      const response = await axios.post(`${API_URL}/api/admin/create-membership-codes`, codesData, { withCredentials: true });
      setCreatedCodes(response.data.codes);
      toast.success(`Se crearon ${response.data.codes.length} códigos`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear códigos');
    } finally { setCreatingCodes(false); }
  };

  const handleExport = async (type) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/export/${type}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_sosfindmed.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Archivo ${type} descargado`);
    } catch (err) { toast.error('Error al exportar'); }
  };

  const viewMembershipDetail = async (id) => {
    setLoadingDetail(true);
    setDetailOpen(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/membership/${id}`, { withCredentials: true });
      setSelectedMembership(response.data);
    } catch (err) {
      toast.error('Error al cargar detalle');
      setDetailOpen(false);
    } finally { setLoadingDetail(false); }
  };

  const handleStatusChange = async () => {
    if (!selectedMembership || !newStatus) return;
    setChangingStatus(true);
    try {
      await axios.put(`${API_URL}/api/admin/membership/${selectedMembership.id}/status`,
        { status: newStatus, reason: statusReason }, { withCredentials: true });
      toast.success(`Estado actualizado a "${newStatus}"`);
      setStatusChangeOpen(false);
      setNewStatus('');
      setStatusReason('');
      setDetailOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar estado');
    } finally { setChangingStatus(false); }
  };

  const handleSendRenewal = async (membershipId) => {
    try {
      await axios.post(`${API_URL}/api/admin/send-renewal-reminder/${membershipId}`, {}, { withCredentials: true });
      toast.success('Recordatorio de renovación enviado');
    } catch (err) { toast.error('Error al enviar recordatorio'); }
  };

  const handleBulkRenewal = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/send-bulk-renewal`, {}, { withCredentials: true });
      toast.success(response.data.message);
    } catch (err) { toast.error('Error al enviar recordatorios'); }
  };

  const handleSendProspectFollowups = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/send-prospect-followups`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al enviar seguimiento'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const copyAllCodes = () => {
    if (createdCodes) {
      navigator.clipboard.writeText(createdCodes.join('\n'));
      toast.success('Todos los códigos copiados');
    }
  };

  const handleRepairMembership = async () => {
    if (!repairEmail.trim()) return;
    setRepairingMembership(true);
    setRepairResult(null);
    try {
      const response = await axios.post(`${API_URL}/api/admin/create-membership-for-user`,
        { email: repairEmail.trim(), status: 'active', sales_channel: 'admin' },
        { withCredentials: true });
      setRepairResult(response.data);
      toast.success(response.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear membresía');
      setRepairResult({ error: err.response?.data?.detail || 'Error' });
    } finally { setRepairingMembership(false); }
  };

  const handleDiagnostic = async () => {
    setLoadingDiagnostic(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/diagnostic`, { withCredentials: true });
      setDiagnostic(response.data);
    } catch (err) {
      toast.error('Error al obtener diagnóstico');
    } finally { setLoadingDiagnostic(false); }
  };

  // Filtered memberships
  const filteredMemberships = memberships.filter(m => {
    const matchesStatus = statusFilter === 'all' || m.status === statusFilter;
    const matchesSearch = !searchQuery ||
      [m.user_name, m.user_email, m.user_phone, m.card_code, m.beneficiary_name || '']
        .join(' ').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 pt-16">
        <Loader2 className="w-8 h-8 animate-spin text-[#C81D25]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12 px-4" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="admin-title">Panel de Administración</h1>
            <p className="text-gray-500">Gestiona y analiza tu negocio SOS FindMed</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={fetchData} className="border-gray-300" data-testid="refresh-admin-data">
              <RefreshCw className="w-4 h-4 mr-2" />Actualizar
            </Button>
            <Select onValueChange={(v) => handleExport(v)}>
              <SelectTrigger className="w-44 border-gray-300" data-testid="export-select">
                <Download className="w-4 h-4 mr-2" /><SelectValue placeholder="Exportar CSV" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="memberships">Membresías</SelectItem>
                <SelectItem value="users">Usuarios</SelectItem>
                <SelectItem value="revenue">Ingresos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Alerts */}
        {expiringSoon.length > 0 && (
          <Alert className="bg-amber-50 border-amber-200" data-testid="expiring-alert">
            <Bell className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>{expiringSoon.length} membresía(s)</strong> por vencer en los próximos 60 días.
              <button onClick={() => setActiveTab('expiring')} className="underline font-semibold ml-1">Ver detalle</button>
            </AlertDescription>
          </Alert>
        )}
        {prospects.length > 0 && (
          <Alert className="bg-red-50 border-red-200" data-testid="prospects-alert">
            <UserX className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <strong>{prospects.length} persona(s)</strong> se registraron pero no completaron su compra.
              <button onClick={() => setActiveTab('prospects')} className="underline font-semibold ml-1">Ver prospectos</button>
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm flex-wrap h-auto p-1">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#C81D25] data-[state=active]:text-white" data-testid="tab-dashboard">
              <CreditCard className="w-4 h-4 mr-1" />Dashboard
            </TabsTrigger>
            <TabsTrigger value="memberships" className="data-[state=active]:bg-[#C81D25] data-[state=active]:text-white" data-testid="tab-memberships">
              <CreditCard className="w-4 h-4 mr-1" />Membresías
            </TabsTrigger>
            <TabsTrigger value="emergencies" className="data-[state=active]:bg-[#C81D25] data-[state=active]:text-white" data-testid="tab-emergencies">
              <ShieldAlert className="w-4 h-4 mr-1" />Emergencias
            </TabsTrigger>
            <TabsTrigger value="expiring" className="data-[state=active]:bg-[#C81D25] data-[state=active]:text-white relative" data-testid="tab-expiring">
              <Calendar className="w-4 h-4 mr-1" />Por Vencer
              {expiringSoon.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">{expiringSoon.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="actions" className="data-[state=active]:bg-[#C81D25] data-[state=active]:text-white" data-testid="tab-actions">
              <UserPlus className="w-4 h-4 mr-1" />Acciones
            </TabsTrigger>
            <TabsTrigger value="clients" className="data-[state=active]:bg-[#C81D25] data-[state=active]:text-white" data-testid="tab-clients">
              <Users className="w-4 h-4 mr-1" />Clientes
            </TabsTrigger>
            <TabsTrigger value="prospects" className="data-[state=active]:bg-[#C81D25] data-[state=active]:text-white relative" data-testid="tab-prospects">
              <UserX className="w-4 h-4 mr-1" />Prospectos
              {prospects.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">{prospects.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="log" className="data-[state=active]:bg-[#C81D25] data-[state=active]:text-white" data-testid="tab-log">
              <AlertCircle className="w-4 h-4 mr-1" />Historial
            </TabsTrigger>
            <TabsTrigger value="referrals" className="data-[state=active]:bg-[#C81D25] data-[state=active]:text-white" data-testid="tab-referrals">
              <Users className="w-4 h-4 mr-1" />Referidos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard"><DashboardTab dashboard={dashboard} conversionStats={conversionStats} /></TabsContent>

          <TabsContent value="memberships">
            <MembershipsTab
              filteredMemberships={filteredMemberships} searchQuery={searchQuery} setSearchQuery={setSearchQuery}
              statusFilter={statusFilter} setStatusFilter={setStatusFilter}
              viewMembershipDetail={viewMembershipDetail} handleSendRenewal={handleSendRenewal}
            />
          </TabsContent>

          <TabsContent value="emergencies"><EmergenciesTab emergencyRequests={emergencyRequests} /></TabsContent>

          <TabsContent value="expiring">
            <ExpiringTab expiringSoon={expiringSoon} handleBulkRenewal={handleBulkRenewal} handleSendRenewal={handleSendRenewal} />
          </TabsContent>

          {/* Actions Tab - kept inline since it has dialog state interactions */}
          <TabsContent value="actions" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Add User */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#C81D25]" />Agregar Usuario</CardTitle>
                  <CardDescription>Registra un cliente desde otro canal de venta</CardDescription>
                </CardHeader>
                <CardContent>
                  <Dialog open={addUserOpen} onOpenChange={(open) => { setAddUserOpen(open); if (!open) { setNewUserResult(null); setNewUserData({ name: '', email: '', phone: '', card_code: '', sales_channel: 'admin' }); } }}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-[#C81D25] hover:bg-[#a01820]" data-testid="add-user-button"><UserPlus className="w-4 h-4 mr-2" />Nuevo Usuario</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Agregar Nuevo Usuario</DialogTitle>
                        <DialogDescription>Registra un usuario desde otro canal de venta</DialogDescription>
                      </DialogHeader>
                      {newUserResult ? (
                        <div className="space-y-4">
                          <Alert className="bg-emerald-50 border-emerald-200">
                            <CheckCircle className="h-4 w-4 text-emerald-600" />
                            <AlertDescription className="text-emerald-800">Usuario creado exitosamente</AlertDescription>
                          </Alert>
                          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                            <div>
                              <p className="text-sm text-gray-500">Email:</p>
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{newUserResult.email}</p>
                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(newUserResult.email)}><Copy className="w-3 h-3" /></Button>
                              </div>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Contraseña temporal:</p>
                              <div className="flex items-center gap-2">
                                <p className="font-mono font-medium bg-white px-2 py-1 rounded border">{newUserResult.temp_password}</p>
                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(newUserResult.temp_password)}><Copy className="w-3 h-3" /></Button>
                              </div>
                            </div>
                          </div>
                          <Button onClick={() => setAddUserOpen(false)} className="w-full">Cerrar</Button>
                        </div>
                      ) : (
                        <form onSubmit={handleAddUser} className="space-y-4">
                          <div className="space-y-2"><Label>Nombre Completo</Label><Input name="name" value={newUserData.name} onChange={(e) => setNewUserData({...newUserData, name: e.target.value})} required data-testid="add-user-name" /></div>
                          <div className="space-y-2"><Label>Correo Electrónico</Label><Input name="email" type="email" value={newUserData.email} onChange={(e) => setNewUserData({...newUserData, email: e.target.value})} required data-testid="add-user-email" /></div>
                          <div className="space-y-2"><Label>Teléfono</Label><Input name="phone" value={newUserData.phone} onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})} data-testid="add-user-phone" /></div>
                          <div className="space-y-2"><Label>Código de Tarjeta</Label><Input name="card_code" value={newUserData.card_code} onChange={(e) => setNewUserData({...newUserData, card_code: e.target.value})} required placeholder="SOSFM-XXXX-XXXX" data-testid="add-user-card-code" /></div>
                          <div className="space-y-2">
                            <Label>Canal de Venta</Label>
                            <Select value={newUserData.sales_channel} onValueChange={(v) => setNewUserData({...newUserData, sales_channel: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Administrador</SelectItem>
                                <SelectItem value="presencial">Venta Presencial</SelectItem>
                                <SelectItem value="telefono">Venta Telefónica</SelectItem>
                                <SelectItem value="distributor">Distribuidor</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button type="submit" className="w-full bg-[#C81D25]" disabled={addingUser} data-testid="submit-add-user">
                            {addingUser ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</> : 'Crear Usuario'}
                          </Button>
                        </form>
                      )}
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>

              {/* Create Codes */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><QrCode className="w-5 h-5 text-[#C81D25]" />Crear Códigos</CardTitle>
                  <CardDescription>Genera códigos para tarjetas físicas y distribuidores</CardDescription>
                </CardHeader>
                <CardContent>
                  <Dialog open={createCodesOpen} onOpenChange={(open) => { setCreateCodesOpen(open); if (!open) setCreatedCodes(null); }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full border-[#C81D25] text-[#C81D25] hover:bg-red-50" data-testid="create-codes-button"><QrCode className="w-4 h-4 mr-2" />Generar Códigos</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Crear Códigos de Membresía</DialogTitle>
                        <DialogDescription>Genera códigos para tarjetas físicas, corporativas o puntos de venta</DialogDescription>
                      </DialogHeader>
                      {createdCodes ? (
                        <div className="space-y-4">
                          <Alert className="bg-emerald-50 border-emerald-200">
                            <CheckCircle className="h-4 w-4 text-emerald-600" />
                            <AlertDescription className="text-emerald-800">Se crearon {createdCodes.length} códigos</AlertDescription>
                          </Alert>
                          <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                            <div className="flex justify-between items-center mb-3">
                              <p className="text-sm font-medium">Códigos generados:</p>
                              <Button variant="ghost" size="sm" onClick={copyAllCodes}><Copy className="w-3 h-3 mr-1" />Copiar todos</Button>
                            </div>
                            <div className="space-y-1">
                              {createdCodes.map((code) => (
                                <div key={code} className="flex items-center justify-between bg-white px-2 py-1 rounded text-sm font-mono border">
                                  <span>{code}</span>
                                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(code)}><Copy className="w-3 h-3" /></Button>
                                </div>
                              ))}
                            </div>
                          </div>
                          <Button onClick={() => setCreateCodesOpen(false)} className="w-full">Cerrar</Button>
                        </div>
                      ) : (
                        <form onSubmit={handleCreateCodes} className="space-y-4">
                          <div className="space-y-2"><Label>Cantidad</Label><Input type="number" min="1" max="100" value={codesData.quantity} onChange={(e) => setCodesData({...codesData, quantity: parseInt(e.target.value) || 1})} data-testid="codes-quantity" /></div>
                          <div className="space-y-2"><Label>Prefijo</Label><Input value={codesData.prefix} onChange={(e) => setCodesData({...codesData, prefix: e.target.value.toUpperCase()})} data-testid="codes-prefix" /></div>
                          <div className="space-y-2">
                            <Label>Canal de Venta</Label>
                            <Select value={codesData.sales_channel} onValueChange={(v) => setCodesData({...codesData, sales_channel: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="physical_card">Tarjeta Física</SelectItem>
                                <SelectItem value="corporate">Corporativa</SelectItem>
                                <SelectItem value="point_of_sale">Punto de Venta</SelectItem>
                                <SelectItem value="distributor">Distribuidor</SelectItem>
                                <SelectItem value="individual">Individual</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button type="submit" className="w-full bg-[#C81D25]" disabled={creatingCodes} data-testid="submit-create-codes">
                            {creatingCodes ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</> : <><Plus className="w-4 h-4 mr-2" />Crear Códigos</>}
                          </Button>
                        </form>
                      )}
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </div>

            {/* Bulk actions */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-amber-500" />Acciones Masivas</CardTitle>
                <CardDescription>Envía comunicaciones a múltiples usuarios</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={handleBulkRenewal} data-testid="bulk-renewal-action">
                    <Mail className="w-4 h-4 mr-2" />Enviar recordatorios de renovación (consumidas + expiradas)
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Repair Membership for existing user */}
            <Card className="border-0 shadow-lg border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-blue-500" />Reparar Membresia</CardTitle>
                <CardDescription>Crea una membresia activa para un usuario existente que ya pago pero no se registro su membresia</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {repairResult && !repairResult.error && (
                  <Alert className="bg-emerald-50 border-emerald-200">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <AlertDescription className="text-emerald-800">
                      {repairResult.message}<br />
                      Codigo: <strong>{repairResult.card_code}</strong> | Expira: {new Date(repairResult.expiry_date).toLocaleDateString('es-MX')}
                    </AlertDescription>
                  </Alert>
                )}
                {repairResult && repairResult.error && (
                  <Alert className="bg-red-50 border-red-200">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">{repairResult.error}</AlertDescription>
                  </Alert>
                )}
                <div className="flex gap-3">
                  <Input
                    placeholder="Email del usuario (ej: karla@gmail.com)"
                    value={repairEmail}
                    onChange={(e) => setRepairEmail(e.target.value)}
                    className="flex-1"
                    data-testid="repair-email-input"
                  />
                  <Button
                    onClick={handleRepairMembership}
                    disabled={!repairEmail.trim() || repairingMembership}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="repair-membership-btn"
                  >
                    {repairingMembership ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" />Crear Membresia</>}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Diagnostic */}
            <Card className="border-0 shadow-lg border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Search className="w-5 h-5 text-amber-500" />Diagnostico de Base de Datos</CardTitle>
                <CardDescription>Verifica el estado de la base de datos y los registros</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={handleDiagnostic} disabled={loadingDiagnostic} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="diagnostic-btn">
                  {loadingDiagnostic ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verificando...</> : <><Search className="w-4 h-4 mr-2" />Ejecutar Diagnostico</>}
                </Button>
                {diagnostic && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(diagnostic.database_counts).map(([key, val]) => (
                        <div key={key} className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-gray-900">{val}</p>
                          <p className="text-xs text-gray-500">{key.replace(/_/g, ' ')}</p>
                        </div>
                      ))}
                    </div>
                    {diagnostic.recent_users?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Usuarios Recientes:</h4>
                        <div className="space-y-1">
                          {diagnostic.recent_users.map(u => (
                            <div key={u.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm">
                              <span>{u.name} ({u.email})</span>
                              <span className={u.has_membership ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {u.has_membership ? 'Con membresia' : 'SIN membresia'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {diagnostic.recent_payments?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Pagos Recientes:</h4>
                        <div className="space-y-1">
                          {diagnostic.recent_payments.map((p, i) => (
                            <div key={i} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm">
                              <span>{p.user_email} — ${p.amount}</span>
                              <span className={p.payment_status === 'paid' ? 'text-emerald-600 font-semibold' : 'text-amber-600'}>{p.payment_status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cleanup */}
            <Card className="border-0 shadow-lg border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-500" />Limpiar Datos de Prueba</CardTitle>
                <CardDescription>Elimina usuarios y membresías de prueba. Solo conserva las cuentas reales que especifiques.</CardDescription>
              </CardHeader>
              <CardContent><CleanupSection onComplete={fetchData} /></CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="clients"><ClientsTab allUsers={allUsers} handleExport={handleExport} /></TabsContent>
          <TabsContent value="prospects"><ProspectsTab prospects={prospects} handleSendProspectFollowups={handleSendProspectFollowups} handleExport={handleExport} /></TabsContent>
          <TabsContent value="log"><ActivityLogTab activityLog={activityLog} /></TabsContent>
          <TabsContent value="referrals"><ReferralsTab referralStats={referralStats} /></TabsContent>
        </Tabs>
      </div>

      {/* Membership Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detalle de Membresía</DialogTitle></DialogHeader>
          {loadingDetail ? (
            <div className="py-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#C81D25]" /></div>
          ) : selectedMembership && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <StatusBadge status={selectedMembership.status} />
                <span className="font-mono text-lg font-bold text-gray-800" data-testid="detail-card-code">{selectedMembership.card_code}</span>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Información del Usuario</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-400">Nombre:</span><p className="font-medium">{selectedMembership.user_name}</p></div>
                  <div><span className="text-gray-400">Email:</span><p className="font-medium">{selectedMembership.user_email}</p></div>
                  <div><span className="text-gray-400">Teléfono:</span><p className="font-medium">{selectedMembership.user_phone || 'N/A'}</p></div>
                  <div><span className="text-gray-400">Canal:</span><p className="font-medium">{channelNames[selectedMembership.sales_channel] || selectedMembership.sales_channel}</p></div>
                </div>
                {selectedMembership.is_minor && (
                  <div className="mt-2 p-2 bg-blue-50 rounded-lg text-sm">
                    <p className="text-blue-800"><strong>Menor de edad</strong></p>
                    <p className="text-blue-600">Beneficiario: {selectedMembership.beneficiary_name}</p>
                    <p className="text-blue-600">Tutor: {selectedMembership.guardian_name}</p>
                  </div>
                )}
              </div>
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Fechas</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-400">Creación:</span><p className="font-medium">{new Date(selectedMembership.created_at).toLocaleDateString('es-MX')}</p></div>
                  <div><span className="text-gray-400">Activación:</span><p className="font-medium">{selectedMembership.activation_date ? new Date(selectedMembership.activation_date).toLocaleDateString('es-MX') : 'N/A'}</p></div>
                  <div><span className="text-gray-400">Expiración:</span><p className="font-medium">{selectedMembership.expiry_date ? new Date(selectedMembership.expiry_date).toLocaleDateString('es-MX') : 'N/A'}</p></div>
                  <div><span className="text-gray-400">Consumo:</span><p className="font-medium">{selectedMembership.consumed_date ? new Date(selectedMembership.consumed_date).toLocaleDateString('es-MX') : 'No consumida'}</p></div>
                </div>
              </div>
              {selectedMembership.ine_url && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide mb-3">INE del Usuario</h4>
                  <img src={selectedMembership.ine_url} alt="INE" className="max-w-full rounded-lg border" data-testid="detail-ine-image" />
                </div>
              )}
              <div className="border-t pt-4 flex flex-wrap gap-3">
                <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => setStatusChangeOpen(true)} data-testid="change-status-btn">
                  <Edit className="w-4 h-4 mr-2" />Cambiar Estado
                </Button>
                {(selectedMembership.status === 'consumed' || selectedMembership.status === 'expired') && (
                  <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => handleSendRenewal(selectedMembership.id)}>
                    <Mail className="w-4 h-4 mr-2" />Enviar Recordatorio
                  </Button>
                )}
                <Button variant="ghost" onClick={() => copyToClipboard(`${selectedMembership.user_name} | ${selectedMembership.user_email} | ${selectedMembership.card_code} | ${selectedMembership.status}`)}>
                  <Copy className="w-4 h-4 mr-2" />Copiar Info
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={statusChangeOpen} onOpenChange={setStatusChangeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar Estado de Membresía</DialogTitle>
            <DialogDescription>
              Estado actual: <strong>{selectedMembership?.status}</strong> — Código: <strong>{selectedMembership?.card_code}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nuevo Estado</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="new-status-select"><SelectValue placeholder="Seleccionar estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activa</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="consumed">Consumida</SelectItem>
                  <SelectItem value="expired">Expirada</SelectItem>
                  <SelectItem value="cancelled">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Razón (opcional)</Label>
              <Input value={statusReason} onChange={(e) => setStatusReason(e.target.value)} placeholder="Motivo del cambio" data-testid="status-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusChangeOpen(false)}>Cancelar</Button>
            <Button className="bg-[#C81D25]" onClick={handleStatusChange} disabled={!newStatus || changingStatus} data-testid="confirm-status-change">
              {changingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar Cambio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
